"use client";
import GenericAutoComplete from "@/components/GenericAutoComplete";
import GenericSelectionModal from "@/components/GenericSelectionModal";
import {
  HOTEL_LOCATION_TYPES,
  defaultHotelGuestConfig,
} from "@/configs/hotel/search";
import { hotelGuestDisplayFormatter } from "@/configs/hotel/search/guestConfig";
import { useModalHelpers } from "@/contexts/ModalContext";
import { useHotelSearch } from "@/hooks/useHotelSearch";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { getPathname, useRouter } from "@/i18n/navigation";
import { formatDateRange, isBefore, isSameDay } from "@/utils/dateFormatter";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChildSelect from "./ChildSelect";
import DateRangePicker from "./DateRangePicker";

const LOCATION_ICONS = {
  locationAndCountry: "fak.city-new",
  hotel: "fak.new-hotel",
  theme: "far.hotel",
};

const locationCategorySorting = ["locationAndCountry", "hotel", "theme"];

const getIcon = (iconClass) => {
  const [module, icon] = iconClass.split(".");

  return byPrefixAndName[module][icon];
};

const getLocationBottomText = (category, location) => {
  switch (category) {
    case "locationAndCountry": {
      return location?.label?.split("-")?.[0];
    }
    case "hotel": {
      return [location?.district, location?.city].filter((l) => l).join(", ");
    }
    case "theme": {
      return null;
    }
  }
};

const calculateNights = (checkinDate, checkoutDate) => {
  if (!checkinDate || !checkoutDate) return 0;
  const checkin = new Date(checkinDate);
  const checkout = new Date(checkoutDate);
  const diffTime = Math.abs(checkout - checkin);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/** Geçmiş tarihli aramada: giriş = yarın, çıkış = yarın + orijinal gece sayısı (dayjs ile yerel tarih) */
const getAdjustedSearchDates = (search) => {
  const checkinRaw = search?.checkinDateRaw;
  if (checkinRaw == null) return null;
  const checkinDay = dayjs(checkinRaw).startOf("day");
  if (!checkinDay.isValid()) return null;
  const todayStart = dayjs().startOf("day");
  if (!checkinDay.isBefore(todayStart)) return null; // Geçmiş değil, ayar gerekmez
  const nights =
    calculateNights(search.checkinDateRaw, search.checkoutDateRaw) || 1;
  const newCheckin = todayStart.add(1, "day");
  const newCheckout = newCheckin.add(nights, "day");
  return {
    checkinDate: newCheckin.toDate(),
    checkoutDate: newCheckout.toDate(),
  };
};

const nightsText = (t, nightCount) => t("nights", { 0: nightCount });

const appendCampaignIdIfExists = (url) => {
  if (!url || typeof window === "undefined") return url;

  const campaignId = new URLSearchParams(window.location.search).get(
    "campaignId",
  );
  if (!campaignId) return url;

  const [pathPart, hashPart] = url.split("#");
  const hasQuery = pathPart.includes("?");
  const params = new URLSearchParams(hasQuery ? pathPart.split("?")[1] : "");

  if (!params.get("campaignId")) {
    params.set("campaignId", campaignId);
  }

  const basePath = hasQuery ? pathPart.split("?")[0] : pathPart;
  const queryString = params.toString();
  const withQuery = queryString ? `${basePath}?${queryString}` : basePath;

  return hashPart ? `${withQuery}#${hashPart}` : withQuery;
};

const Form = ({
  initialLocationCode,
  initialCheckinDate,
  initialCheckoutDate,
  initialGuests,
  hideLatestSearches,
  scrollToForm,
  /** Buton title override (opsiyonel) */
  searchButtonTitle,
  /** Otel için: autocomplete kapatılıp tıklanmadan arama yapılırsa, ilk öneri arka planda kullanılsın (input'a yazılmaz) */
  autoSelectFirstWhenClosed = false,
  /** Sticky search (listing) için ViewportHeightWrapper aktif edilsin mi? */
  viewportHeightWrapperActive = false,
} = {}) => {
  const locale = useLocale();
  const t = useTranslationDetector("hotelSearch");
  const tForm = useTranslationDetector("hotelSearch");
  const router = useRouter();

  // Hotel-specific storage key
  const STORAGE_KEY = "latestHotelSearches";

  // Initial data objesini oluştur (flight search ile aynı mantık)
  const initialData = {};
  if (initialLocationCode)
    initialData.initialLocationCode = initialLocationCode;
  if (initialCheckinDate) initialData.checkinDate = initialCheckinDate;
  if (initialCheckinDate) initialData.checkoutDate = initialCheckoutDate;
  if (initialGuests) initialData.guests = initialGuests;
  initialData.storageKey = STORAGE_KEY;

  // Use hotel search hook
  const {
    location,
    checkinDate,
    checkoutDate,
    guests,
    latestHotelSearches,
    popularLocations,
    ensurePopularLoaded,
    setCheckinDate,
    setCheckoutDate,
    setGuests,
    onSelectLocation,
    validateForm,
    generateHotelSearchUrl,
    publicHolidays,
    searchForHotelLocations,
    getGuestInfoText,
    getLatestHotelSearches,
    generateQuery,
    refreshPublicHolidays,
  } = useHotelSearch(initialData);

  const isMobile = useIsMobile();
  const [validationErrors, setValidationErrors] = useState({});
  const guestsModalRef = useRef(null);
  const dateSelectionRef = useRef(null);
  const lastDatesRef = useRef({
    checkin: checkinDate || null,
    checkout: checkoutDate || null,
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [activeDateInput, setActiveDateInput] = useState(null); // 'checkin' or 'checkout'

  const { warningModal } = useModalHelpers();
  const hasShownChildrenAgesModalRef = useRef(false);

  // Herhangi bir portal açıksa body scroll'u kilitle
  const shouldLockBodyScroll = isMobile && isDatePickerOpen;
  useLockBodyScroll(shouldLockBodyScroll);

  const guestConfig = useMemo(() => {
    return defaultHotelGuestConfig;
  }, []);

  const openGuestsModal = useCallback(() => {
    guestsModalRef.current?.open();
  }, []);

  const closeGuestsModal = useCallback(() => {
    guestsModalRef.current?.close();
  }, []);

  const handleApplyValidationError = useCallback(() => {
    closeGuestsModal();
    warningModal({
      title: t("childrenAgesRequiredTitle"),
      message: t("childrenAgesRequiredMessage"),
      isBottomSheet: true,
      onClose: openGuestsModal,
    });
  }, [warningModal, t, openGuestsModal, closeGuestsModal]);

  // child age validation - önce konuk seçimini kapat, uyarı göster; uyarı kapatılınca konuk seçimini tekrar aç
  useEffect(() => {
    const shouldShow =
      validationErrors.childrenAges &&
      Object.keys(validationErrors).length === 1;

    if (shouldShow && !hasShownChildrenAgesModalRef.current) {
      hasShownChildrenAgesModalRef.current = true;
      closeGuestsModal();
      warningModal({
        title: t("childrenAgesRequiredTitle"),
        message: t("childrenAgesRequiredMessage"),
        isBottomSheet: true,
        onClose: openGuestsModal,
      });
    } else if (!shouldShow) {
      hasShownChildrenAgesModalRef.current = false;
    }
    // t, warningModal vb. değişebilir; yalnızca validationErrors değiştiğinde çalışması yeterli
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationErrors]);

  const handleLocationSelect = useCallback(
    (location) => {
      onSelectLocation(location);
      // Location seçildiğinde error'ı temizle
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.location;
        return newErrors;
      });
    },
    [onSelectLocation],
  );

  const clearLocationSelection = useCallback(() => {
    onSelectLocation(null);
  }, [onSelectLocation]);

  /** Dropdown tıklamadan kapatıldığında ilk öneriyi seç ve input'a yaz */
  const handleCloseWithoutSelection = useCallback(
    (results) => {
      if (!autoSelectFirstWhenClosed || !results?.length) return;
      const first = Array.isArray(results) ? results[0] : results?.data?.[0];
      if (first) handleLocationSelect(first);
    },
    [autoSelectFirstWhenClosed, handleLocationSelect],
  );

  const searchForHotelLocationsForAutoComplete = useCallback(
    (query) => searchForHotelLocations(query),
    [searchForHotelLocations],
  );

  const lastGuestChangeRef = useRef(null);

  const handleGuestSelect = useCallback(
    (values) => {
      const nextStr = JSON.stringify(values || {});
      lastGuestChangeRef.current = nextStr;
      setGuests({ guests: values });
    },
    [setGuests],
  );

  const handleGuestChange = useCallback(
    (values) => {
      const nextStr = JSON.stringify(values || {});
      if (lastGuestChangeRef.current === nextStr) return;
      lastGuestChangeRef.current = nextStr;
      setGuests({ guests: values });
    },
    [setGuests],
  );

  // Handle search history item selection
  const handleSearchHistorySelect = useCallback(
    (search) => {
      // Fill all form fields with the selected search data
      if (search.location) {
        onSelectLocation(search.location);
      }
      const adjusted = getAdjustedSearchDates(search);
      if (adjusted) {
        setCheckinDate(adjusted.checkinDate);
        setCheckoutDate(adjusted.checkoutDate);
      } else {
        if (search.checkinDateRaw) {
          setCheckinDate(new Date(search.checkinDateRaw));
        }
        if (search.checkoutDateRaw) {
          setCheckoutDate(new Date(search.checkoutDateRaw));
        }
      }
      if (search.guests) {
        handleGuestSelect(search.guests);
      }

      // Clear any validation errors
      setValidationErrors({});
    },
    [onSelectLocation, setCheckinDate, setCheckoutDate],
  );

  const onSelectCheckoutDate = useCallback(
    (date, triggerDate) => {
      // triggerDate varsa onu kullan, yoksa date.from kullan (flight search mantığı)
      const clickedDate = triggerDate || date?.from;
      if (!clickedDate) return;

      // Giriş/çıkış hangi inputtan açılmış olursa olsun: ilk tıklanan tarih giriş, ikinci çıkış.

      // Edge case: Her iki tarih de dolu ve kullanıcı mevcut giriş veya çıkış gününe tıkladı → yeni seçim başlat
      if (
        checkinDate &&
        checkoutDate &&
        (isSameDay(clickedDate, checkinDate) ||
          isSameDay(clickedDate, checkoutDate))
      ) {
        setCheckinDate(clickedDate);
        setCheckoutDate(null);
        return;
      }

      // Normal range seçimi devam ediyor
      if (!checkinDate) {
        // İlk seçim
        setCheckinDate(clickedDate);
        setCheckoutDate(null);
        // Error'ları temizle
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.checkinDate;
          return newErrors;
        });
      } else if (!checkoutDate) {
        // İkinci seçim - checkout date belirleniyor
        if (isBefore(clickedDate, checkinDate)) {
          // Geriye doğru seçim → Sadece girişi güncelle, çıkışı kullanıcı seçsin; datepicker açık kalsın
          setCheckinDate(clickedDate);
          setCheckoutDate(null);
        } else if (isSameDay(clickedDate, checkinDate)) {
          // Aynı gün seçilmişse → Checkout'u 1 gün sonrasına set et (otel aynı gün giriş-çıkış yapılamaz)
          const nextDay = new Date(checkinDate);
          nextDay.setDate(nextDay.getDate() + 1);
          setCheckoutDate(nextDay);
          if (!isMobile) dateSelectionRef.current?.close();
        } else {
          // İleriye doğru seçim (en az 1 gün sonra) → Checkout'u set et
          setCheckoutDate(clickedDate);
          if (!isMobile) dateSelectionRef.current?.close();
        }
        // Error'ları temizle
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.checkinDate;
          delete newErrors.checkoutDate;
          return newErrors;
        });
      } else {
        // Her iki tarih de dolu → Yeni seçim başlat
        setCheckinDate(clickedDate);
        setCheckoutDate(null);
        // Error'ları temizle
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.checkinDate;
          delete newErrors.checkoutDate;
          return newErrors;
        });
      }
    },
    [setCheckoutDate, setCheckinDate, checkinDate, checkoutDate, isMobile],
  );

  const handleDateOpen = useCallback(() => {
    // Tarih seçici açılırken mevcut değerleri snapshot olarak sakla
    lastDatesRef.current = {
      checkin: checkinDate,
      checkout: checkoutDate,
    };
    refreshPublicHolidays();
  }, [checkinDate, checkoutDate, refreshPublicHolidays]);

  const handleDateCancel = useCallback(() => {
    // Çarpıya basıldığında snapshot alınan değerlere geri dön
    const { checkin, checkout } = lastDatesRef.current || {};
    setCheckinDate(checkin || null);
    setCheckoutDate(checkout || null);
  }, [setCheckinDate, setCheckoutDate]);

  // Hangi input'a tıklandığını takip et
  const handleDateInputFocus = useCallback((inputType) => {
    setActiveDateInput(inputType);
  }, []);

  const handleSearchHistorySelectSearch = (searchData) => {
    const adjusted = getAdjustedSearchDates(searchData);
    const dataToUse = adjusted
      ? {
          ...searchData,
          checkinDateRaw: adjusted.checkinDate,
          checkoutDateRaw: adjusted.checkoutDate,
        }
      : searchData;
    const url = appendCampaignIdIfExists(generateQuery(dataToUse));

    if (url) {
      const localizedPath = getPathname({ href: url, locale });
      window.location.href = localizedPath;
    }
  };

  // Render function for default content (when not searching)
  const renderDefaultContent = useCallback(
    ({ onSelectItem }) => {
      return (
        <div className="flex flex-col gap-5 text-sm">
          {latestHotelSearches?.length > 0 && (
            <div className="flex flex-col items-start justify-between gap-2">
              <span className="text-center text-gray-500">
                {tForm("recentSearches")}
              </span>
              <div className="flex w-full flex-col items-start justify-between gap-4">
                {latestHotelSearches.map((search, index) => (
                  <button
                    className="flex w-full cursor-pointer items-start justify-between gap-6"
                    onClick={() => {
                      if (search.location) {
                        // Önce tüm formu doldur (lokasyon + tarih + misafir)
                        handleSearchHistorySelect(search);
                        // Sonra lokasyonu autocomplete'e seçtir (input güncellenir, dropdown kapanır)
                        onSelectItem(search.location);
                      }
                    }}
                    key={index}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-5 w-5 items-center justify-center">
                        <FontAwesomeIcon
                          className="!h-4 !w-4"
                          icon={byPrefixAndName.far["clock-rotate-left"]}
                        />
                      </div>
                      <div className="flex flex-col gap-1 text-left">
                        <div className="flex items-center justify-start gap-[6px] font-semibold">
                          <span>
                            {search.location.value.split("-")[0].trim()}
                          </span>
                        </div>
                        <div className="text-foreground-muted">
                          {formatDateRange(
                            search.checkinDateRaw,
                            search.checkoutDateRaw,
                            { locale, format: "DD MMM" },
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Popular destinations */}
          {!popularLocations || popularLocations.length === 0 ? (
            <div className="flex flex-col items-start justify-between gap-2">
              <span className="text-center text-gray-500">
                {tForm("popularSearches")}
              </span>
              <div className="flex w-full flex-wrap gap-3">
                {/* Placeholder chips */}
                <span className="bg-background-400 h-8 w-24 animate-pulse rounded-lg" />
                <span className="bg-background-400 h-8 w-20 animate-pulse rounded-lg" />
                <span className="bg-background-400 h-8 w-28 animate-pulse rounded-lg" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-start justify-between gap-2">
              <span className="text-center text-gray-500">
                {tForm("popularSearches")}
              </span>
              <div className="flex w-full flex-wrap gap-3">
                {popularLocations.map((item, index) => (
                  <button
                    key={index}
                    className="border-border cursor-pointer rounded-lg border px-3 py-[6px] text-base"
                    onClick={() => onSelectItem(item.AutoCompleteResult)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    },
    [latestHotelSearches, popularLocations, tForm, handleSearchHistorySelect],
  );

  // Render function for search results
  const renderSearchResults = useCallback(({ searchResults, onSelectItem }) => {
    if (searchResults?.length === 0) {
      return (
        <div className="flex flex-col gap-4 text-sm">
          <div className="flex flex-col items-center justify-center gap-3 p-10 text-sm">
            <div className="flex flex-col gap-2">
              <span className="text-center text-lg font-semibold">
                {tForm("noResultsFound")}
              </span>
              <span className="text-center text-sm">
                {tForm("changeSearchCriteria")}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (searchResults?.length > 0) {
      const mappedSearchLocationCategories = searchResults.reduce(
        (acc, curr) => {
          if (!acc[HOTEL_LOCATION_TYPES[curr.type]]) {
            acc[HOTEL_LOCATION_TYPES[curr.type]] = [];
          }

          acc[HOTEL_LOCATION_TYPES[curr.type]].push(curr);

          return acc;
        },
        {},
      );

      return (
        <div className="flex flex-col gap-4 text-sm">
          {Object.keys(mappedSearchLocationCategories)
            .sort(
              (a, b) =>
                locationCategorySorting.indexOf(a) +
                9999 -
                (locationCategorySorting.indexOf(b) + 9999),
            )
            .map((category) => (
              <div
                className="flex flex-col gap-3"
                key={`search_category-${category}`}
              >
                <span className="text-primary-muted-subtext text-base font-normal">
                  {t(`${category}`)}
                </span>

                {mappedSearchLocationCategories[category].map(
                  (searchResult) => (
                    <button
                      className="flex cursor-pointer items-start justify-between gap-6"
                      key={searchResult.id}
                      onClick={() => onSelectItem(searchResult)}
                    >
                      <div className="flex items-center gap-4 text-left">
                        <FontAwesomeIcon
                          fontSize={18}
                          icon={getIcon(LOCATION_ICONS[category])}
                        />
                        <div className="flex flex-col items-start gap-1">
                          <div className="flex items-center justify-start gap-[6px] font-semibold">
                            {searchResult.value}
                          </div>
                          <div className="text-foreground-muted">
                            {getLocationBottomText(category, searchResult)}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {searchResult.code}
                      </div>
                    </button>
                  ),
                )}
              </div>
            ))}
        </div>
      );
    }

    return null;
  }, []);

  const validateAndSearch = useCallback(() => {
    // ÖNCE location kontrolü yap - eğer yoksa direkt hata göster ve çık
    const hasValidLocation =
      location &&
      location.id &&
      location.id !== undefined &&
      location.id !== null &&
      location.id !== "";

    if (!hasValidLocation) {
      const errors = validateForm();
      errors.location = errors.location || "Lokasyon seçimi zorunludur";
      setValidationErrors(errors);
      return;
    }

    const errors = validateForm();

    if (Object.keys(errors).length === 0) {
      setValidationErrors({});

      const newSearch = {
        location,
        checkinDateRaw: checkinDate,
        checkinDate: checkinDate?.toLocaleDateString("tr-TR"),
        checkoutDateRaw: checkoutDate,
        checkoutDate: checkoutDate?.toLocaleDateString("tr-TR"),
        guests,
      };

      // Mevcut arama geçmişini al
      const existingSearches = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || "[]",
      );

      const sameValueIndex = existingSearches.findIndex(
        (s) => s.location?.id === newSearch.location?.id,
      );

      if (sameValueIndex > -1) {
        existingSearches.splice(sameValueIndex, 1);
      }

      // Yeni aramayı array'in başına ekle
      const updatedSearches = [newSearch, ...existingSearches];

      // localStorage'a kaydet
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSearches));

      const url = generateHotelSearchUrl();
      const urlWithCampaign = appendCampaignIdIfExists(url);

      if (!urlWithCampaign) {
        const newErrors = validateForm();
        setValidationErrors(newErrors);
        return;
      }

      const localizedPath = getPathname({ href: urlWithCampaign, locale });
      window.location.href = localizedPath;
    } else {
      // Validasyon başarısız - hataları göster
      setValidationErrors(errors);
    }
  }, [
    validateForm,
    location,
    checkinDate,
    checkoutDate,
    generateHotelSearchUrl,
    locale,
    getGuestInfoText,
    guests,
  ]);

  const handleRemoveHistoryItem = (index) => {
    const existingSearches = JSON.parse(
      localStorage.getItem(STORAGE_KEY) || "[]",
    );

    existingSearches.splice(index, 1);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existingSearches));
    getLatestHotelSearches();
  };
  return (
    <div className="z-10 flex flex-col gap-4 md:gap-[30px]">
      <div
      // className="max-lg:container"
      >
        <div className="shadow-medium flex flex-col gap-0 rounded-xl bg-white md:gap-6 md:rounded-none md:bg-transparent md:shadow-none">
          <div className="flex w-full flex-col md:flex-row md:gap-4">
            <div className="md:shadow-medium flex flex-col items-center rounded-lg bg-white shadow-none md:w-4/4 md:flex-row">
              <div className="hotel-location-autocomplete-container relative z-[1] flex w-full flex-col items-end md:static md:w-[53.8%] md:flex-row md:items-center">
                {" "}
                {/* AutoComplete - Lokasyon */}
                <div className="w-full [&_.web-dropdown]:max-h-[500px] [&_.web-dropdown]:w-full [&_.web-dropdown]:overflow-auto">
                  <GenericAutoComplete
                    id="hotel-location"
                    label={tForm("searchBarLocation")}
                    customInputPlaceholder={tForm("locationPlaceholder")}
                    placeholderPortalInput={tForm("locationPlaceholder")}
                    searchFunction={searchForHotelLocationsForAutoComplete}
                    onSelect={handleLocationSelect}
                    onClearSelection={clearLocationSelection}
                    value={location?.value}
                    error={validationErrors.location}
                    translationNamespace="hotelSearch"
                    renderDefaultContent={renderDefaultContent}
                    onOpen={ensurePopularLoaded}
                    onCloseWithoutSelection={handleCloseWithoutSelection}
                    renderSearchResults={renderSearchResults}
                    inputClassName="font-semibold placeholder:font-medium placeholder:text-foreground-muted"
                    scrollToForm={scrollToForm}
                    viewportHeightWrapperActive={viewportHeightWrapperActive}
                    //renderNoResults={renderNoResults}
                    debounce={300}
                    minQueryLength={3}
                  />
                </div>
              </div>

              <span className="bg-border mx-1 hidden h-[34px] w-[1px] md:block"></span>

              {/* Date Selection - Oneway */}
              <div className="border-primary-weak flex w-full items-center border-b md:flex-1 md:border-0">
                {" "}
                <div className="w-full">
                  <DateRangePicker
                    ref={dateSelectionRef}
                    id="hotel-date"
                    label={tForm("dateSelection")}
                    onSelect={onSelectCheckoutDate}
                    value={{
                      from: checkinDate,
                      to: checkoutDate,
                    }}
                    disabled={checkinDate}
                    onPortalStateChange={setIsDatePickerOpen}
                    defaultMonth={checkinDate}
                    checkinDate={checkinDate}
                    checkoutDate={checkoutDate}
                    publicHolidays={publicHolidays}
                    onInputFocus={handleDateInputFocus}
                    error={
                      validationErrors.checkinDate ||
                      validationErrors.checkoutDate
                    }
                    containerClassName="!border-b-0"
                    scrollToForm={scrollToForm}
                    onOpen={handleDateOpen}
                    onCancel={handleDateCancel}
                    viewportHeightWrapperActive={viewportHeightWrapperActive}
                  />
                </div>
              </div>
            </div>

            {/* Guest Selection */}
            <div className="hotel-guest-selection-container w-full md:w-1/4 md:max-w-[200px]">
              <GenericSelectionModal
                ref={guestsModalRef}
                config={guestConfig}
                scrollToForm={scrollToForm}
                label={tForm("guestTitle")}
                placeholder={tForm("guestSelect")}
                value={guests}
                onSelect={handleGuestSelect}
                onChange={handleGuestChange}
                onApplyValidationError={handleApplyValidationError}
                displayFormatter={(values) =>
                  hotelGuestDisplayFormatter(values, tForm)
                }
                applyButtonText={tForm("guestApply")}
                renderFooter={(props) => (
                  <ChildSelect
                    childCount={props.tempValues.child}
                    tempValues={props.tempValues}
                    setTempValues={props.setTempValues}
                  />
                )}
                // selectionItemsClassName="divide-y divide-primary-weak"
                // className="[&_.selection-container~div]:scrollbar-hide [&_.selection-button]:font-semibold [&_.selection-container~div]:max-h-[545px] [&_.selection-container~div]:overflow-y-auto [&_.selection-footer]:border-none"
                className="[&_.selection-button]:font-semibold [&_.selection-footer]:border-none"
                selectionItemsClassName="max-h-[300px] overflow-y-auto divide-y divide-primary-weak"
              />
            </div>

            {/* Search Button */}
            <div className="p-4 md:p-0">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  validateAndSearch();
                }}
                id="hotel-search-button"
                className="bg-primary hover:bg-primary/90 hover:shadow-soft inline-flex h-[50px] w-full cursor-pointer items-center justify-center rounded-lg p-4 font-semibold text-white transition-all duration-200 ease-in-out md:h-[66px] lg:w-[66px] lg:text-lg xl:w-[200px] xl:gap-2"
              >
                {/* Yazı: Mobile ve Desktop'ta göster, Tablet'te gizle */}
                <span className="inline whitespace-nowrap md:hidden xl:inline">
                  {searchButtonTitle ?? tForm("searchButton")}
                </span>

                {/* Turna icon: Mobile ve Desktop'ta göster, Tablet'te gizle */}
                <span className="hide-on-completed-page flex">
                  <FontAwesomeIcon
                    className="!hidden h-6! w-6! max-md:!inline-block xl:!inline-block"
                    icon={byPrefixAndName.fak["turna-logo-icon"]}
                  />
                </span>

                {/* Büyüteç: Sadece Tablet'te göster */}
                <FontAwesomeIcon
                  className="inline-block h-[32px]! w-[32px]! max-md:!hidden xl:!hidden"
                  icon={byPrefixAndName.fak["search-button-icon"]}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {!hideLatestSearches && latestHotelSearches?.length > 0 && (
        <div className="flex flex-col gap-4">
          <div className="hidden font-semibold md:block">
            {t("latestSearches")}
          </div>

          <div className="scrollbar-hide overflow-x-auto">
            <div className="flex w-max gap-4">
              {latestHotelSearches.map((search, index) => {
                const nightCount = calculateNights(
                  search?.checkinDateRaw,
                  search?.checkoutDateRaw,
                );

                return (
                  <div
                    className="shadow-small flex items-center rounded-[10px] bg-white"
                    key={index}
                  >
                    <button
                      onClick={() => handleSearchHistorySelectSearch(search)}
                      key={index}
                      className="flex cursor-pointer items-center gap-3 py-3 pr-2 pl-3"
                    >
                      <FontAwesomeIcon
                        className="text-primary !h-5 !w-5"
                        icon={byPrefixAndName.fak["new-hotel"]}
                      />

                      <div className="flex flex-col gap-1 text-left">
                        <div className="flex items-center justify-start gap-[6px] text-sm whitespace-nowrap">
                          <span className="max-w-[92px] truncate">
                            {search.location.value.split("-")[0].trim()}
                          </span>
                          -
                          {nightCount > 0 && (
                            <span>{nightsText(t, nightCount)}</span>
                          )}
                          <span>{getGuestInfoText(search?.guests)}</span>
                        </div>
                      </div>
                    </button>

                    <button
                      className="flex cursor-pointer items-center pr-3"
                      onClick={() => handleRemoveHistoryItem(index)}
                    >
                      <FontAwesomeIcon
                        className="text-[#909294]"
                        fontSize={16}
                        icon={byPrefixAndName.far["xmark"]}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Form;
